import type { BlockComponentStateId, BlockComponentType } from '../../types/block';
import type { ConditionEvaluationObject } from '../../types/conditionEvaluationObject';
import { FileVisibility } from '../../types/fileVisibility';
import type { StyleObject } from '../../types/styleObject';

export type TemplateFile = {
  label?: string;
  icon?: string;
  source?: 'url' | 'file';
  url?: string;
};

export enum UploadError {
  UnSupportedType = 'UnSupportedType',
  DuplicateFile = 'DuplicateFile',
  FileSize = 'FileSize',
  SaveError = 'SaveError',
  BlockedByPolicy = 'BlockedByPolicy',
}

export { FileVisibility };

type FileRestrictions = {
  /**
   * The file types that are supported for upload.
   *
   * Supported values: '*', 'audio/*', '.csv', 'application/*',
   * '.xlsx', '.xls', '.xlsb', '__folder__', '.ico', 'image/*', 'image/jpeg',
   * 'image/jpg', '.json', '.lottie', 'application/json', 'text/markdown', '.msg', '.pdf',
   * 'text/plain', '.png', '.pptx', '.ppt', '.svg', 'text/*', 'video/*',
   * '.doc, .docx', '.xml', '.xsd', '.zip'
   *
   * @see {@link ../contentSchema.ts#supportedFileTypes} for the canonical list with titles.
   */
  supportedFileTypes?: string[];
  /*
   * The variant of the selection of files (single or multiple)
   */
  selectionVariant?: 'single' | 'multiple';
  /*
   * The maximum file size for the upload
   */
  maxFileSize?: number;
  /*
   * The maximum number of files that can be uploaded
   */
  maxFiles?: number;
};

type AccessOptions = {
  /**
   * Determines who can access the uploaded file
   */
  fileVisibility?: FileVisibility;
  allowDownload?: boolean;
};

export type FileUploadContent = {
  label?: string;
  /**
   * @deprecated Use addOns.description instead
   */
  description?: string;
  addOns?: {
    templateFile?: TemplateFile;
    description?: {
      content?: string;
    };
  };
  /**
   * @deprecated Use fileVisibility instead
   */
  allowPublicAccess?: boolean;
} & FileRestrictions &
  AccessOptions;

export type FileUploadContentFormData = Omit<
  FileUploadContent,
  | 'supportedFileTypes'
  | 'selectionVariant'
  | 'maxFileSize'
  | 'maxFiles'
  | 'fileVisibility'
  | 'allowDownload'
  | 'description'
  | 'allowPublicAccess'
> & {
  fileRestrictions?: FileRestrictions;
  accessOptions?: AccessOptions;
};

export type FileUploadAppearance = {
  uploadIcon?: string;
  disabled?: ConditionEvaluationObject;
  styles?: Pick<StyleObject, 'backgroundColor' | 'borderRadius' | 'borderColor' | 'borderWidth'>;
};

export type FileUploadComponentType = BlockComponentType<
  'FileUpload',
  FileUploadContent,
  FileUploadAppearance
>;

export type FileType = {
  id: string;
  name: string;
  url: string;
  /**
   * Preferred URL sourceType, CLOUD_STORAGE is deprecated
   * URL: source will be url
   * CLOUD_STORAGE: source will be key, which is not present for non s3 multipart types (azure, xhr)
   */
  sourceType: 'CLOUD_STORAGE' | 'URL';
  source?: string;
  fileType?: string;
  size?: number;
  type: 'FILE';
  mimeType?: string;
  progress?: number | undefined;
  error?: UploadError;
  isUploaded?: boolean;
  extension?: string;
  meta?: Record<string, unknown>;
  hasError?: boolean;
};

export type FileUploadBlockStateType = BlockComponentStateId &
  Pick<FileUploadComponentType, 'content' | 'appearance' | 'componentType'> & {
    files: FileType[];
    removeFileId?: string;
    isUploading?: boolean;
  };
